"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * About 1054 — KUK home "About Us".
 *
 * Port of kuk-campus-portal `components/Home/AboutUs.tsx`: a tinted band with a
 * click-to-play YouTube facade on one side and a ruled heading, a justified
 * paragraph and a "Know More" outline button on the other.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px and `lg:` is 1024px. This app remaps `md:` to 1230px and exposes
 * 768px as `tab:` (see globals.css `--breakpoint-*`), so the reference's `md:`
 * is ported as `tab:` and its `lg:` as this app's `sm:` (1080px).
 *
 * THE FACADE — v1 does not embed the player on load. It shows the video's
 * `hqdefault.jpg` poster with a play button and only swaps in the iframe on
 * click, which keeps YouTube's ~1MB of player JS off the critical path. That is
 * the right behaviour and is reproduced, including the reserved heights (377px
 * desktop / 204px mobile) that stop the swap shifting the page.
 *
 * THE VIDEO ID — v1 extracts it with `video.split("v=")[1]`, which returns
 * everything after the FIRST `v=` — so a URL with a trailing `&t=` or a
 * `youtu.be/` short link silently produces a broken embed. Parsed properly here:
 * `watch?v=`, `youtu.be/`, `/embed/` and `/shorts/` all resolve, and a bare
 * 11-character id is accepted as-is.
 *
 * TWO HEADINGS, ONE STRING — v1 prints the heading twice, once `md:hidden` above
 * the row and once `hidden md:block` inside the text column, so the mobile
 * heading sits above the video and the desktop one beside it. Both are the same
 * string; reproduced as two placements of one CMS field.
 *
 * NOT CARRIED OVER — v1's `router.push("/about")` on the button. The
 * destination is a CMS field here, and the button can instead open the lead
 * popup by turning `is_cta` on, which is how every other CTA in this app works.
 */

/** v1's `.custom-underline`: `bottom:-6px; left:0; width:33%; height:5px`. */
const HEADING_UNDERLINE = "absolute -bottom-1.5 left-0 h-[5px] w-1/3";

interface About1054Data {
  title?: string;
  title_text_color?: string;
  /** The accent rule under the heading. */
  highlight_border_color?: string;
  description?: string;
  description_text_color?: string;
  /** YouTube watch / short / embed URL, or a bare 11-character id. */
  video_link?: string;
  /** Optional poster override; empty falls back to YouTube's `hqdefault.jpg`. */
  desktop_image?: string;
  alt_image?: string;
  /** Band behind the section. */
  background_color?: string;
  read_more_text?: string;
  read_more_color?: string;
  cta_link?: string;
  /** On = open the lead popup instead of following `cta_link`. */
  is_cta?: boolean;
  status?: boolean;
}

/**
 * Accepts `watch?v=`, `youtu.be/`, `/embed/`, `/shorts/` and a bare id.
 * Returns "" when nothing that looks like an id is present.
 */
function youTubeId(url: string | undefined): string {
  const raw = (url || "").trim();
  if (!raw) return "";
  if (/^[\w-]{11}$/.test(raw)) return raw;
  const patterns = [
    /[?&]v=([\w-]{11})/,
    /youtu\.be\/([\w-]{11})/,
    /\/embed\/([\w-]{11})/,
    /\/shorts\/([\w-]{11})/,
  ];
  for (const p of patterns) {
    const m = raw.match(p);
    if (m) return m[1];
  }
  return "";
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

const About1054 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as About1054Data | undefined;
  const dispatch = useDispatch();
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const {
    title = "",
    title_text_color = "#031F44",
    highlight_border_color = "#9F1C33",
    description = "",
    description_text_color = "#031F44",
    video_link = "",
    desktop_image = "",
    alt_image = "",
    background_color = "#F8F8F8",
    read_more_text = "Know More",
    read_more_color = "#031F44",
    cta_link = "",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);
  const isCta = isCmsShowFlagOn(sectionData?.is_cta, false);

  const videoId = useMemo(() => youTubeId(video_link), [video_link]);
  const poster =
    desktop_image || (videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : "");

  const handleCtaClick = useCallback(() => {
    ctaClick({
      section: "about_1054",
      component: "read_more",
      label: read_more_text?.trim() || "know_more",
      step: "",
      entity: "about_1054",
      parentEntity: "about_1054",
    });
    if (isCta) {
      dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
    }
  }, [dispatch, isCta, read_more_text]);

  if (!status) return null;

  const ctaClassName = cn(
    "inline-flex h-10 w-full items-center justify-center rounded-md border",
    "bg-transparent px-5 text-sm font-medium shadow-sm transition-colors",
    "tab:w-[152px]",
  );
  const ctaStyle = { color: read_more_color, borderColor: read_more_color };

  const Heading = ({ className }: { className?: string }) =>
    title ? (
      <h2
        className={cn("relative w-fit text-2xl font-bold tab:text-4xl", className)}
        style={{ color: title_text_color }}
      >
        {title}
        <span
          aria-hidden
          className={HEADING_UNDERLINE}
          style={{ backgroundColor: highlight_border_color }}
        />
      </h2>
    ) : null;

  /** v1 reserves 377px on desktop and 204px on mobile so the swap cannot shift the page. */
  const VideoPane = ({ variant }: { variant: "desktop" | "mobile" }) => {
    const isDesktop = variant === "desktop";
    if (!videoId) return null;
    return (
      <div
        className={cn(
          "w-full items-center justify-center",
          isDesktop ? "hidden tab:flex tab:py-12" : "flex tab:hidden",
        )}
        style={{ minHeight: isDesktop ? 377 : 204 }}
      >
        {isVideoLoaded ? (
          <iframe
            width={500}
            height={isDesktop ? 377 : 204}
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
            title={title || "Video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
            className="w-auto max-w-full"
          />
        ) : (
          <button
            type="button"
            aria-label={`Play ${title || "video"}`}
            onClick={() => setIsVideoLoaded(true)}
            className={cn(
              "relative flex cursor-pointer items-center justify-center",
              isDesktop ? "size-[400px] max-w-full" : "h-[204px] w-full",
            )}
          >
            {poster && (
              <Image
                src={poster}
                alt={alt_image || title || "Video thumbnail"}
                width={400}
                height={400}
                loading="lazy"
                className="size-full object-cover"
              />
            )}
            <span
              aria-hidden
              className="absolute rounded-full bg-black/50 p-2 text-white"
            >
              ▶
            </span>
          </button>
        )}
      </div>
    );
  };

  return (
    <section className="w-full" style={{ backgroundColor: background_color }}>
      <div className="relative mx-auto w-full max-w-[1120px] overflow-hidden">
        <div className="w-full p-10">
          <Heading className="mb-10 tab:hidden" />

          <div className="flex flex-col items-center justify-around gap-5 sm:flex-row">
            <VideoPane variant="desktop" />
            <VideoPane variant="mobile" />

            <div className="flex w-full flex-col items-start justify-around space-y-5">
              <Heading className="hidden tab:block" />
              <p
                className="text-justify text-sm font-light leading-7"
                style={{ color: description_text_color, minHeight: 112 }}
              >
                {description}
              </p>
              {read_more_text &&
                (isCta || !cta_link ? (
                  <button
                    type="button"
                    onClick={handleCtaClick}
                    className={ctaClassName}
                    style={ctaStyle}
                  >
                    {read_more_text}
                  </button>
                ) : (
                  <Link
                    href={normalizeHref(cta_link)}
                    onClick={handleCtaClick}
                    className={ctaClassName}
                    style={ctaStyle}
                  >
                    {read_more_text}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About1054;
